import { useEffect } from 'react'
import { Phone, LogOut, User } from 'lucide-react'
import useSipStore from './stores/sipStore'
import useUserStore from './stores/userStore'
import StatusBadge from './components/StatusBadge'
import Dialpad from './components/Dialpad'
import CallHistory from './components/CallHistory'
import Recordings from './components/Recordings'
import LogViewer from './components/LogViewer'
import TestRunner from './components/TestRunner'
import SettingsPanel from './components/SettingsPanel'
import PowerDialer from './components/PowerDialer'
import Phonebook from './components/Phonebook'
import Login from './components/Login'
import UsageBadge from './components/UsageBadge'

export default function App() {
  const user = useUserStore(s => s.user)
  const logout = useUserStore(s => s.logout)
  const status = useSipStore(s => s.status)
  const connect = useSipStore(s => s.connect)
  const disconnect = useSipStore(s => s.disconnect)

  // Auto-connect when a user is logged in and we're disconnected
  useEffect(() => {
    if (user && status === 'disconnected') connect()
  }, [user])

  const handleLogout = () => {
    if (!confirm('להתנתק?')) return
    disconnect()
    logout()
  }

  // Not logged in → show login screen
  if (!user) return <Login />

  return (
    <div className="min-h-screen bg-bg text-text">
      {/* Header */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center">
              <Phone size={20} className="text-brand" />
            </div>
            <div>
              <div className="font-bold text-lg">KaliDialer</div>
              <div className="text-xs text-mute">
                {user.business?.name || 'חייגן עצמאי'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <UsageBadge />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-line/30 text-sm">
              <div className="w-6 h-6 rounded-full bg-brand/20 text-brand text-xs font-semibold flex items-center justify-center">
                {user.name?.slice(0, 1) || '?'}
              </div>
              <div className="hidden sm:block">
                <div className="text-xs font-medium leading-tight">{user.name}</div>
                <div className="text-[10px] text-mute ltr tech">שלוחה {user.extension}</div>
              </div>
            </div>
            <StatusBadge />
            <SettingsPanel />
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-line/30 text-mute hover:text-text"
              title="התנתק"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Dialpad + Tests */}
        <div className="lg:col-span-3 space-y-4">
          <Dialpad />
          <TestRunner />
        </div>

        {/* Middle-left: Phonebook + Power Dialer */}
        <div className="lg:col-span-3 space-y-4">
          <Phonebook />
          <PowerDialer />
        </div>

        {/* Middle-right: History + Recordings */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-panel border border-line rounded-2xl p-4">
            <CallHistory />
          </div>
          <div className="bg-panel border border-line rounded-2xl p-4">
            <Recordings />
          </div>
        </div>

        {/* Right: Logs */}
        <div className="lg:col-span-3">
          <LogViewer />
          <div className="mt-3 text-xs text-mute leading-relaxed">
            <div className="font-semibold mb-1">איך להשתמש:</div>
            <ol className="space-y-1 list-decimal list-inside">
              <li>וודא שהסטטוס "מחובר" (ירוק)</li>
              <li>הקלד מספר ולחץ "חייג"</li>
              <li>אם משהו לא עובד - הרץ את הבדיקות משמאל</li>
              <li>בלוגים מימין תראה הכל בזמן אמת</li>
            </ol>
          </div>
        </div>
      </main>
    </div>
  )
}
